import { createSlice } from '@reduxjs/toolkit';
import { DocumentCategory, DocumentFile } from 'modules/types/Document';
import { UwtType } from 'modules/types/LeasingCalculatorCodes';
import { fetchDocuments, fetchDocumentTypes, uploadDocuments } from './thunks';

export interface DocumentCategoryState {
  loading: boolean;
  documents?: DocumentFile[];
  error?: string;
}

export interface DocumentTypeState {
  loading: boolean;
  data?: UwtType[];
  error?: string;
}

export interface DocumentationState {
  categories: { [key in DocumentCategory]?: DocumentCategoryState };
  uploadLoading: boolean;
  uploadError?: string;
  uploadSuccessful?: boolean;
  documentTypes: DocumentTypeState;
}

const initialCategoryState: DocumentCategoryState = {
  loading: false,
  documents: [],
  error: undefined,
};

const initialState: DocumentationState = {
  categories: {
    [DocumentCategory.KASKO_INSURANCE_POLICY]: { ...initialCategoryState },
    [DocumentCategory.AO_POLICY]: { ...initialCategoryState },
    [DocumentCategory.PROPERTY_POLICY]: { ...initialCategoryState },
    [DocumentCategory.COMPENSATION_CLAIM]: { ...initialCategoryState },
    [DocumentCategory.CONTRACT_TERMINATION]: { ...initialCategoryState },
    [DocumentCategory.EARLY_CONTRACT_TERMINATION]: { ...initialCategoryState },
  },
  uploadLoading: false,
  uploadError: undefined,
  uploadSuccessful: false,
  documentTypes: {
    loading: false,
    data: undefined,
    error: undefined,
  },
};

export const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    uploadFinished: (state) => {
      state.uploadSuccessful = undefined;
      state.uploadError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDocuments.pending, (state, action) => {
      const { documentCategory } = action.meta.arg;

      if (documentCategory) {
        state.categories[documentCategory] = {
          loading: true,
          documents: [],
          error: undefined,
        };
      }
    });
    builder.addCase(fetchDocuments.fulfilled, (state, action) => {
      const { documentCategory } = action.meta.arg;

      if (documentCategory) {
        state.categories[documentCategory] = {
          loading: false,
          documents: action.payload.documents,
          error: undefined,
        };
      }
    });
    builder.addCase(fetchDocuments.rejected, (state, action) => {
      const { documentCategory } = action.meta.arg;

      if (documentCategory) {
        state.categories[documentCategory] = {
          loading: false,
          error: action.payload
            ? (action.payload as Error)?.message
            : action.error?.message,
        };
      }
    });

    builder.addCase(uploadDocuments.pending, (state, action) => {
      state.uploadLoading = true;
      state.uploadSuccessful = false;
      state.uploadError = undefined;
    });
    builder.addCase(uploadDocuments.fulfilled, (state, action) => {
      state.uploadLoading = false;
      state.uploadSuccessful = true;
      state.uploadError = undefined;
    });
    builder.addCase(uploadDocuments.rejected, (state, action) => {
      state.uploadLoading = false;
      state.uploadError =
        'Prijenos dokumenata je bio neuspješan, provjerite dokumente i pokušajte ponovo.';
    });

    builder.addCase(fetchDocumentTypes.pending, (state, action) => {
      state.documentTypes.loading = true;
      state.documentTypes.data = undefined;
      state.documentTypes.error = undefined;
    });
    builder.addCase(fetchDocumentTypes.fulfilled, (state, action) => {
      state.documentTypes.loading = false;
      state.documentTypes.data = action.payload.documentTypes.data;
    });
    builder.addCase(fetchDocumentTypes.rejected, (state, action) => {
      state.documentTypes.loading = false;
      state.documentTypes.error = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
    });
  },
});

export const { uploadFinished } = documentsSlice.actions;

export default documentsSlice.reducer;
